import { NurseFullRole, type NurseInShift } from "@/utils/models";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;
const DAY_PARTS = ["morning", "evening"] as const;

export const areRolesMatch = (role1: NurseFullRole, role2: NurseFullRole): boolean => {
	if (role1 === role2) {
		return true;
	}

	if (role1 === NurseFullRole.RN) {
		return role2 === NurseFullRole.LPN || role2 === NurseFullRole.LVN;
	}
	if (role2 === NurseFullRole.RN) {
		return role1 === NurseFullRole.LPN || role1 === NurseFullRole.LVN;
	}

	return false;
};

export function indexToDay(index: number | undefined): (typeof DAY_NAMES)[number] {
	return DAY_NAMES[index ?? 6] || DAY_NAMES[6];
}

export function indexToDayPart(index: number | undefined): (typeof DAY_PARTS)[number] {
	return DAY_PARTS[index ?? 1] || DAY_PARTS[1];
}

export const normalizeMessage = (msg: string | null | undefined): string | null => {
	return msg === "" || msg == null || msg === undefined ? null : msg;
};

export const compareMessages = (editMessage: string, nurseMessage: string | undefined): boolean => {
	// Convert empty values (null, undefined, '') to null for comparison
	return normalizeMessage(editMessage) !== normalizeMessage(nurseMessage);
};

/** What discarding a pending shift undoes. */
export enum PendingDiscardKind {
	Move = "move",
	Remove = "remove",
	Add = "add",
}

/** A move also names the day its other end is on. */
export type PendingDiscard = { kind: PendingDiscardKind.Move; originalDay: number } | { kind: PendingDiscardKind.Remove } | { kind: PendingDiscardKind.Add };

/**
 * Imports also fill `originalDay` on ordinary shifts, so only a shift pending a move is discarded as a move. A pending
 * move without its original day can't be undone, so it throws rather than falling back to another kind.
 */
export const pendingDiscardOf = (shift: Pick<NurseInShift, "id" | "isPendingToMove" | "isPendingToDelete" | "originalDay">): PendingDiscard => {
	if (shift.isPendingToMove) {
		if (typeof shift.originalDay !== "number") throw new Error(`Pending move of shift ${shift.id} has no original day`);
		return { kind: PendingDiscardKind.Move, originalDay: shift.originalDay };
	}
	return shift.isPendingToDelete ? { kind: PendingDiscardKind.Remove } : { kind: PendingDiscardKind.Add };
};
